import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import PlayersSkeletonLoader from './PlayersSkeletonLoader';
import PlayerStatsLoader from './PlayersStatsLoader';
import LevelAndUserButton from './LevelAndUserButton';
// this component displays a list of players and their statistics
function Players() {
  const [activeUsers, setActiveUsers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [animatedStats, setAnimatedStats] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  
 
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch the list of players from the backend
  useEffect(() => {
    setLoading(true);
    fetch('/server/crazy-cup-server/getPlayers.php')
      .then((response) => response.json())
      .then((data) => {
        setActiveUsers(data);
      })
      .catch((error) => console.error('Error fetching players:', error))
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 200);
      });
  }, []);

  // Fetch player statistics when a player is clicked
  const handlePlayerClick = (playerId) => {
    setSelectedPlayer(playerId);
    setIsModalOpen(true);
    setModalLoading(true);
  
    const requestBody = { clerk_user_id: playerId };
  
    // Fetch the player's statistics from getUserStats.php
    fetch('/server/crazy-cup-server/getUserStats.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setPlayerStats(data.stats);
          startCountUpAnimation(data.stats);
        } else {
          console.error('Error fetching stats:', data.message);
        }
      })
      .catch((error) => console.error('Error fetching player stats:', error))
      .finally(() => {
        setTimeout(() => {
          setModalLoading(false);
        }, 200);
      });
  };

  // Function to start the count-up animation
  const startCountUpAnimation = (targetStats) => {
    const duration = 2000;
    const frameRate = 30;
    const totalFrames = Math.round((duration / 1000) * frameRate);

    const initialStats = {
      total_games_played: 0,
      total_holes_played: 0,
      total_shots_taken: 0,
      average_score_per_round: 0,
      average_shots_per_hole: 0,
      total_hole_in_ones: 0,
      hole_in_one_percentage: 0,
      over_under_par: 0,
    };

    setAnimatedStats(initialStats);

    Object.keys(targetStats).forEach((key) => {
      if (typeof targetStats[key] === 'number') {
        let frame = 0;
        const interval = setInterval(() => {
          frame++;
          const progress = frame / totalFrames;
          const easedProgress = easeOutQuad(progress);
          const currentValue = Math.round(targetStats[key] * easedProgress);

          setAnimatedStats((prev) => ({
            ...prev,
            [key]: currentValue,
          }));

          if (frame === totalFrames) {
            clearInterval(interval);
          }
        }, 1000 / frameRate);
      }
    });
  };

  // Easing function for smooth animation
  const easeOutQuad = (t) => t * (2 - t);

  const closeModal = () => {
    setIsModalOpen(false);
    setPlayerStats(null);
    setAnimatedStats(null);
  };

  return (
    <div id="players-page" className="page">
      <h1 className='page-title'>PLAYERS</h1>
      <div className="group-selection">
        <select className="group-select">
          <option value="" disabled selected>Select a group</option>
          <option value="all">All Players</option>
        </select>
      </div>  
      {loading ? (
        <PlayersSkeletonLoader />
      ) : activeUsers.length === 0 ? (
        <p>There are no active players.</p>
      ) : (
        <div className="players-container"> 
          {activeUsers
            .filter((u) => !user || u.clerk_user_id !== user.id)
            .map((user) => {
              const firstLetter = user.username && user.username.length > 0
                ? user.username[0].toUpperCase()
                : 'P';
              // Use the profile_pic field from the backend, fallback to placeholder if missing
              const avatarUrl = user.profile_pic && user.profile_pic.trim() !== ""
                ? user.profile_pic
                : `https://ui-avatars.com/api/?name=${firstLetter}&background=cccccc&color=ffffff&rounded=true&size=150`;
              return (
                <div
                  key={user.clerk_user_id}
                  className="player-card"
                  onClick={() => handlePlayerClick(user.clerk_user_id)}
                  style={{
                    outline: `3px solid ${user.theme}`,
                  }}
                >
                  <img
                    src={avatarUrl}
                    className="player-avatar"
                    style={{
                      borderRadius: '50%',
                      width: '70px',
                      height: '70px',
                      objectFit: 'cover',
                      background: '#ccc',
                      display: 'block',
                      marginLeft: '-12px',
                    }}
                    alt="Profile"
                  />
                  <h3>{user.username.toUpperCase()}</h3>
                  <h4>Level {user.level}</h4>
                </div>
              );
            })}
        </div>
      )}

      <button id="back-to-main" className="back-to-main" onClick={() => navigate('/')}>
        Back
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 id="user-stats-title">
              {modalLoading
                ? 'LOADING...'
                : `${playerStats?.username.toUpperCase()}'S STATISTICS`}
            </h2>
            {modalLoading ? (
              <PlayerStatsLoader />
            ) : animatedStats ? (
              <div className="user-stats-grid-container">
                <div className="user-stats-grid">
                  <div>
                    Total Rounds<br />
                    <span className="stat-figure">
                      {animatedStats.total_games_played}
                    </span>
                  </div>
                  <div>
                    Total Holes<br />
                    <span className="stat-figure">
                      {animatedStats.total_holes_played}
                    </span>
                  </div>
                  <div>
                    Total Shots<br />
                    <span className="stat-figure">
                      {animatedStats.total_shots_taken}
                    </span>
                  </div>
                  <div>
                    Round Avg<br />
                    <span className="stat-figure">
                      {animatedStats.average_score_per_round.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    Hole Avg<br />
                    <span className="stat-figure">
                      {animatedStats.average_shots_per_hole.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    Hole-in-Ones<br />
                    <span className="stat-figure">
                      {animatedStats.total_hole_in_ones}
                    </span>
                  </div>
                  <div>
                    Hole-in-One %<br />
                    <span className="stat-figure">
                      {Math.round(animatedStats.hole_in_one_percentage)}%
                    </span>
                  </div>
                  <div>
                    Over/Under<br />
                    <span className="stat-figure">
                      {animatedStats.over_under_par > 0
                        ? `+${animatedStats.over_under_par}`
                        : animatedStats.over_under_par}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p>Loading statistics...</p>
            )}
            <button className="userstatsbutton" onClick={closeModal}>
              <span className="close-modal-text">Close</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Players;